export type BrowserInfo = {
    name: string;
    version: string;
    /**
     * `navigator.product`, which every modern browser hardcodes to "Gecko", falling back to the vendor.
     * Carried over verbatim because it is what the warehouse's deviceMake column has always held.
     */
    vendor: string | undefined;
};

/**
 * Ordered most specific first: Opera and Edge user agents also contain `Chrome`, and Chrome's contains
 * `Safari`, so an earlier match has to win.
 */
const USER_AGENT_PATTERNS: RegExp[] = [
    /.+(Opera[\s[A-Z]*|OPR[\sA-Z]*)\/([0-9.]+).*/i,
    /.+(Trident|Edge|Edg|EdgA|EdgiOS)\/([0-9.]+).*/i,
    /.+(Chrome|CriOS|Firefox|FxiOS)\/([0-9.]+).*/i,
    /.+(Safari)\/([0-9.]+).*/i,
    /.+(AppleWebKit)\/([0-9.]+).*/i,
    /.*([A-Z]+)\/([0-9.]+).*/i,
];

/**
 * Safari reports its own version in a `Version/` token and uses the `Safari/` token for the WebKit
 * build, so matching on `Safari/` alone yields 605.1.15 rather than 18.5.
 */
const SAFARI_VERSION_PATTERN = /Version\/([0-9.]+)/i;

/**
 * Browser identity from the user agent, or undefined outside a browser.
 *
 * Names and the vendor fallback are derived the way Amplify derived the Pinpoint endpoint demographic,
 * so the warehouse's device columns keep the values they have always held.
 */
export const getBrowserInfo = (): BrowserInfo | undefined => {
    if (typeof navigator === 'undefined') return undefined;

    const userAgent = navigator.userAgent;
    if (!userAgent) return undefined;

    const vendor = navigator.product || navigator.vendor || undefined;

    for (const pattern of USER_AGENT_PATTERNS) {
        const match = pattern.exec(userAgent);
        if (!match) continue;

        const name = match[1];
        const safariVersion = name.toLowerCase() === 'safari' ? SAFARI_VERSION_PATTERN.exec(userAgent) : null;

        return { name, version: safariVersion ? safariVersion[1] : match[2], vendor };
    }

    return undefined;
};
